import React, {Fragment, useRef, useState} from 'react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {getTranslation} from '~/localization/translation'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {IconProductBrand} from '~/icons/icon-product-brand'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import {ChosenByCheckIcon} from '~/icons/product-page/chosen-by-check-icon'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import VariantsIndicationNewLayout from '../badges/variants-indication-new-layout'
import ProductCardBadge from '../badges/card-placement-badge'
import SaleEventBadge from '../badges/saleEventBadge'

export interface ProductCardProps {
  index: number
  product: Product
}

export const SponsoredProductCardSpecsLayout: FunctionComponent<ProductCardProps> = ({product, index}) => {
  const {
    image = '',
    name,
    brand = '',
    affiliateLink,
    discount,
    badge,
    isSellerProduct,
    asin,
    price,
    imageVariants,
    forcedPosition,
  } = product

  const {locale, language, featureFlags, trackAnalytics, currencyCode, deviceType, domain, hostname, renderTimestamp} =
    useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse: {productsCategory} = {} as SlugApiResponse,
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()

  const {styles: stylesBydomain, domainName} = getDomainConfig(domain, locale)

  const [currentIndex, setCurrentIndex] = useState(0)

  const startX = useRef(0)
  const deltaX = useRef(0)

  const shouldTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )
  const showVariantsIndication =
    featureFlags?.showVariantsIndication && product?.variationDetails && product?.variationDetails.length > 0

  const images = imageVariants ? [image, ...imageVariants] : [image]
  const imageVariantsByDevice = deviceType === 'desktop' ? images?.slice(0, 3) : images?.slice(0, 5)

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportWidgetClick = useReportWidgetClick()

  const reportClickout = useSendPartnerProductClickout({
    product,
    index,
    locale,
    listName: 'sponsored-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })

  const [cardRef] = useReportItemSeen(
    {
      item_list_id: productsCategory?.slug,
      item_name: name,
      item_source: 'widget',
      partner_item_brand: brand,
      partner_item_id: asin,
      affiliation: affiliateLink.type,
      is_dws: isSellerProduct,
      price: price?.amount,
      currency: price?.currency,
      click_element_name: 'sponsored-product',
      index,
      variation: JSON.stringify({
        hasVariantIndication: showVariantsIndication,
        forcedPosition,
      }),
    },
    {},
  )

  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'sponsored-product-card',
      widget_place: 'top',
      variation: JSON.stringify({
        forcedPosition,
      }),
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (startX.current === null) return
    deltaX.current = e.touches[0].clientX - startX.current
  }
  const handleTouchEnd = () => {
    if (Math.abs(deltaX.current) > 50) {
      if (deltaX.current > 0) {
        prevSlide()
      } else {
        nextSlide()
      }
    }
    startX.current = 0
    deltaX.current = 0
  }
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % imageVariantsByDevice?.length)
  }
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? imageVariantsByDevice?.length - 1 : prevIndex - 1))
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: name,
            image: imageVariantsByDevice,
            description: name,
            brand: {
              '@type': 'Brand',
              name: brand,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price?.amount,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: brand ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>

      <div
        ref={cardRef}
        data-testid={`product-card-${index}`}
        className={clsx(
          'border-gray relative z-20 flex h-full min-h-52 w-full flex-col overflow-hidden rounded-3xl border bg-white shadow transition-all duration-300 ease-in-out',
          stylesBydomain.LISTPAGE_SPONSORED_CARD_BORDER,
          'listpage_sponsored_card_border',
        )}
      >
        {/* -------------------------------------------------------------------- BADGES ON TOP WRAPPER -------------- */}
        <div className="badges flex flex-row gap-4 px-10 tablet:justify-between tablet:px-5">
          {/* ----------------------------------------------------------------- Product placement badge -------------- */}
          <ProductCardBadge index={index} badge={badge} className="overflow-hidden rounded-b-lg rounded-t-none" />
        </div>
        {/* ------------------------------------------------------------------------ CARD MAIN CONTENT -------------- */}
        <div className="relative mb-5 mt-2 flex h-full flex-row items-center px-8 tablet:mb-0 tablet:flex-col tablet:px-0">
          <div className="flex w-full flex-row items-center gap-5 tablet:flex-col tablet:gap-0">
            {/* --------------------------------------------------------------------------- Name Mobile ------------------------ */}
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="hidden px-10 tablet:block tablet:px-5"
            >
              <h2 className="line-clamp-2 overflow-hidden text-base font-light leading-7 no-underline">{name}</h2>
            </a>
            {/* ------------------------------------------------------------------------- Images Courusel ----------------------- */}

            <div className="relative flex flex-col justify-center gap-4 border-r border-r-[#CBD5E1] desktop:min-w-56 desktop:pr-5 tablet:flex-col tablet:gap-6 tablet:border-none">
              <div className="flex justify-center overflow-hidden">
                <div
                  className="flex size-52 tablet:transition-transform tablet:duration-500"
                  style={{
                    transform: `translateX(-${currentIndex * 100}%)`,
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {imageVariantsByDevice?.map((image, index) => (
                    <Fragment key={`${image}-${index}`}>
                      <a
                        href={redirectUrl}
                        onClick={handleCardClick}
                        aria-label={`view ${index + 1} image of ${brand}`}
                        target="_blank"
                        rel="sponsored noreferrer"
                        className="flex size-52 flex-shrink-0 items-center justify-center"
                      >
                        <img
                          src={image}
                          alt={`Slide ${index + 1}`}
                          className="size-44 flex-shrink-0 object-scale-down"
                        />
                      </a>
                    </Fragment>
                  ))}
                </div>
              </div>

              {imageVariantsByDevice && imageVariantsByDevice.length > 1 ? (
                <div className="flex flex-row items-center justify-between tablet:justify-center tablet:space-x-5">
                  {imageVariantsByDevice?.map((image, index) => (
                    <button
                      type="button"
                      key={`${image}-${index}`}
                      onClick={() => setCurrentIndex(index)}
                      className={clsx(
                        'flex flex-shrink-0 items-center justify-center rounded-sm desktop:size-14 desktop:border desktop:border-neutral-50 desktop:bg-inherit desktop:p-0.5 desktop:shadow-elegant tablet:h-[13px] tablet:w-[13px] tablet:rounded-full',
                        currentIndex === index
                          ? `${stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY} listpage_button_new_layout_primary`
                          : 'bg-[#E2E8F0]',
                      )}
                    >
                      <img alt={image} src={image} className="size-14 flex-shrink-0 object-scale-down tablet:hidden" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {/* --------------------------------------------------------------------------- Brand Mobile------------------------ */}

            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="mr-auto hidden flex-row items-center gap-2 px-5 tablet:my-2 tablet:flex"
            >
              <IconProductBrand />
              <p
                className="line-clamp-1 text-lg font-bold capitalize tablet:text-base tablet:font-medium"
                data-testid="category-card-brand"
              >
                {brand}
              </p>
            </a>

            {showVariantsIndication && (
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="mr-auto hidden px-10 tablet:my-2 tablet:block tablet:px-5"
              >
                <VariantsIndicationNewLayout
                  className="line-clamp-1 flex w-full flex-row gap-1"
                  index={index + 1}
                  variations={product?.variationDetails}
                  reportVariantsClicked={() => {
                    reportWidgetClick({
                      widget_name: 'variants-indication',
                      widget_place: `card-${index + 1}`,
                      trigger_type: 'click',
                      variation: 'product-card',
                    })
                  }}
                />
              </a>
            )}
            {/* --------------------------------------------------------------------------- INFO ------------------------ */}

            <div className="flex h-full w-full flex-col gap-5">
              {/* --------------------------------------------------------------------------- Brand Desktop ------------------------ */}

              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="flex flex-row items-center gap-2 tablet:hidden"
              >
                <IconProductBrand />
                <p
                  className="line-clamp-1 text-lg font-bold capitalize tablet:text-base tablet:font-medium"
                  data-testid="category-card-brand"
                >
                  {brand}
                </p>
              </a>
              <div className="flex flex-row">
                {/* --------------------------------------------------------------------------- Name Desktop ------------------------ */}
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className="pr-3 tablet:hidden"
                >
                  <h2 className="line-clamp-4 overflow-hidden text-lg font-semibold leading-7 no-underline">{name}</h2>
                </a>
                {/* --------------------------------------------------------------------------------- CTA ------------------------- */}
                <div className="ml-auto flex h-full w-full max-w-[334px] flex-shrink-0 flex-col items-center justify-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-xl tablet:max-w-full tablet:gap-1 tablet:rounded-none tablet:bg-[#F1F5F9] tablet:shadow-none">
                  {/* --------------------------------------------------------------------- MARKETPLACEs CTA WRAPPER ----------------------- */}
                  <div className="flex w-full flex-col gap-3">
                    {/* -------------------------------------------------------------------------- Amazon cta -------------------------------- */}
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                      className={clsx(
                        'relative flex w-full flex-row justify-start gap-4 rounded-3xl p-2.5',
                        stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY,
                        'listpage_button_new_layout_primary',
                      )}
                    >
                      <div
                        className={clsx(
                          'absolute top-2.5 flex shrink-0 items-center justify-center rounded-2xl px-2 py-0.5',
                          stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_BG,
                          stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_ICON,
                          stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_TEXT,
                          'listpage_badge_cta_marketplace_logo_bg listpage_badge_cta_marketplace_logo_text listpage_badge_cta_marketplace_logo_icon',
                        )}
                      >
                        <AmazonLogoIcon
                          className={clsx(
                            'mt-1 h-4 shrink-0',
                            domainName === 'bestestaubsauger' ||
                              (domainName === 'bestexpertschoice' && locale.toLowerCase() === 'ca')
                              ? 'w-14'
                              : 'w-11',
                          )}
                          domainName={domainName}
                        />
                      </div>
                      <p className="mx-auto text-base font-semibold capitalize">
                        {translations.ProductCard.checkPrice}
                      </p>
                      {discount ? (
                        <div
                          className={clsx(
                            'absolute right-2.5 top-2.5 ml-auto flex items-center justify-center rounded-2xl px-2 py-1 text-xs lowercase',
                            stylesBydomain.LISTPAGE_BADGE_CTA_DISCOUNT_BG,
                            stylesBydomain.LISTPAGE_BADGE_CTA_DISCOUNT_TEXT,
                            'listpage_badge_cta_discount_bg listpage_badge_cta_discount_text',
                          )}
                        >
                          {translations.Badge.discountAmount(discount)}&nbsp;
                          {translations.Badge.off}
                        </div>
                      ) : null}
                    </a>
                  </div>

                  {product.chosenBy && index === 0 ? (
                    <div className="mx-auto mt-3 flex flex-row items-center gap-1">
                      <ChosenByCheckIcon className="hidden tablet:block" />
                      <p className="text-sm font-light text-[#64748B]">
                        {translations.Badge.chosenBy(product.chosenBy)}
                      </p>
                    </div>
                  ) : null}
                  {!!discount && (
                    <SaleEventBadge
                      redirectUrl={redirectUrl}
                      handleCardClick={handleCardClick}
                      brand={brand}
                      index={index}
                      placement="spec-card"
                    />
                  )}
                </div>
              </div>

              {/* --------------------------------------------------------------------------- Variants Desktop ------------------------ */}
              {showVariantsIndication && (
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className="rounded-md border border-[#CBD5E1] bg-[#F8FAFC] px-2 py-1.5 tablet:hidden"
                >
                  <VariantsIndicationNewLayout
                    className="line-clamp-1 flex w-full flex-row gap-1"
                    index={index + 1}
                    variations={product?.variationDetails}
                    reportVariantsClicked={() => {
                      reportWidgetClick({
                        widget_name: 'variants-indication',
                        widget_place: `card-${index + 1}`,
                        trigger_type: 'click',
                        variation: 'product-card',
                      })
                    }}
                  />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
